import { FormEvent, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import { useGuicheDeleteStyles } from "./styles";
import { UserInScheduleWithPermissions } from "../../../../../interfaces/user";
import { useUsersStyles } from "../../../styles";
import AddCircle from "@material-ui/icons/AddCircle";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apisiseci } from "../../../../../services/api";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useAvailableProfiles } from "../../../../../services/api-tansquery/user/profile-management";
import { useAvailableOrgaos } from "../../../../../services/api-tansquery/user/available-orgaos";
import { useAuth } from "../../../../../contexts/auth";

type Props = {
    user: UserInScheduleWithPermissions;
    buttonTrigger?: React.ReactNode;
}

export default function AddProfileButton({ user, buttonTrigger }: Props) {
    const [open, setOpen] = useState(false);
    const [profile, setProfile] = useState("");
    const [orgao, setOrgao] = useState("");
    const [allowMultipleOrgaos, setAllowMultipleOrgaos] = useState(false);
    const [requiresMultipleOrgaosConfirmation, setRequiresMultipleOrgaosConfirmation] = useState(false);
    const { classes } = useGuicheDeleteStyles();
    const { classes: defaultClasses } = useUsersStyles();
    const { adminUser } = useAuth();
    const { data: availableProfilesData } = useAvailableProfiles();
    const { data: availableOrgaosData } = useAvailableOrgaos();
    const queryClient = useQueryClient();
    const token = localStorage.getItem("gov_access_token_sso");
    
    // Verificar se o usuário já tem o perfil de atendente em algum órgão
    const userHasAtendenteProfile = user.permissions?.includes('Atendente') || false;

    const mutation = useMutation({
        mutationKey: ["add-profile", user.id],
        mutationFn: async (data: { profile: string; orgao_id?: string; allow_multiple_orgaos?: boolean }) => {
            if (!token) throw new Error("Token de autenticação não encontrado.");
            const response = await apisiseci.post(
                "/profile-management/add-profile/",
                {
                    user_id: user.id,
                    profile_name: data.profile,
                    orgao_id: data.orgao_id,
                    allow_multiple_orgaos: data.allow_multiple_orgaos,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        },
        onSuccess: async () => {
            toast.success("Perfil adicionado com sucesso para o usuário.");
            await queryClient.invalidateQueries({ queryKey: ["user-profiles", user.id] });
            await queryClient.invalidateQueries({ queryKey: ["profile", "details", String(user.id)] });
            await queryClient.refetchQueries({ queryKey: ["profile", "details", String(user.id)] });
            setOpen(false);
        },
        onError: (error: AxiosError) => {
            const errorData = (error?.response?.data as any);
            const errorMessage = errorData?.error || "Algo deu errado, tente novamente!";
            
            // Se o erro indica que precisa confirmar múltiplos órgãos
            if (errorData?.requires_multiple_orgaos_confirmation) {
                setRequiresMultipleOrgaosConfirmation(true);
                // Não mostrar toast de erro, apenas mostrar o checkbox
            } else {
                toast.error(errorMessage);
            }
            console.log(error);
        },
    });

    async function handleSubmit(event: FormEvent<HTMLDivElement>) {
        event.preventDefault();

        if (mutation.isLoading || !profile) {
            return;
        }

        const needsOrgao = profile !== 'Administrador de Atendimento';
        
        if (needsOrgao && !orgao) {
            toast.error("Selecione um órgão para este perfil.");
            return;
        }

        await mutation.mutateAsync({ 
            profile, 
            orgao_id: needsOrgao ? orgao : undefined,
            allow_multiple_orgaos: profile === 'Atendente' && userHasAtendenteProfile ? allowMultipleOrgaos : undefined
        });
    }

    useEffect(() => {
        if (open && availableProfilesData?.available_profiles?.[0]) {
            setProfile(availableProfilesData.available_profiles[0].name);
        }
    }, [open, availableProfilesData]);

    useEffect(() => {
        if (open) {
            const availableOrgaosFromAPI = availableOrgaosData?.available_orgaos || [];
            
            if (availableOrgaosFromAPI.length > 0 && availableOrgaosFromAPI[0]) {
                setOrgao(String(availableOrgaosFromAPI[0].id));
            }
            // Resetar estados quando abrir o dialog
            setAllowMultipleOrgaos(false);
            setRequiresMultipleOrgaosConfirmation(false);
        }
    }, [open, availableOrgaosData]);

    const availableProfiles = availableProfilesData?.available_profiles || [];
    
    const availableOrgaos = availableOrgaosData?.available_orgaos || [];
    const needsOrgao = profile !== 'Administrador de Atendimento';

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle fontSize="small" />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Adicionar Perfil
                </Button>
            )}
            <Dialog open={open} maxWidth="sm" scroll="paper">
                <Card component="form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={
                            <Typography variant="h5">
                                Adicionar Perfil
                            </Typography>
                        }
                    />
                    <CardContent className={classes.cardContent}>
                        <FormControl component="fieldset" style={{ marginBottom: 16 }}>
                            <FormLabel component="legend">Perfil</FormLabel>
                            <RadioGroup
                                value={profile}
                                onChange={e => setProfile(e.target.value)}
                            >
                                {availableProfiles.map((profileOption) => (
                                    <FormControlLabel
                                        key={profileOption.id}
                                        value={profileOption.name}
                                        control={<Radio color="primary" />}
                                        label={profileOption.display_name}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>

                        {needsOrgao && (
                            <FormControl fullWidth style={{ marginBottom: 16 }}>
                                <InputLabel>Órgão</InputLabel>
                                <Select
                                    value={orgao}
                                    onChange={e => setOrgao(e.target.value as string)}
                                    label="Órgão"
                                >
                                    {availableOrgaos.map((orgaoOption) => (
                                        <MenuItem key={orgaoOption.id} value={String(orgaoOption.id)}>
                                            {orgaoOption.sigla} - {orgaoOption.nome}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {profile === 'Atendente' && userHasAtendenteProfile && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={allowMultipleOrgaos}
                                        onChange={(e, checked) => {
                                            setAllowMultipleOrgaos(checked);
                                            setRequiresMultipleOrgaosConfirmation(false);
                                        }}
                                    />
                                }
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span>O atendente pode atender em múltiplos locais de atendimento?</span>
                                        <Tooltip 
                                            title="Marque caso os atendentes desse órgão possam atender em múltiplos locais de atendimento"
                                            arrow
                                        >
                                            <HelpOutlineIcon fontSize="small" style={{ color: '#706E7A', cursor: 'help' }} />
                                        </Tooltip>
                                    </div>
                                }
                                style={{ marginTop: 16, marginBottom: 16 }}
                            />
                        )}

                        {requiresMultipleOrgaosConfirmation && (
                            <Typography color="error" style={{ marginTop: 8, marginBottom: 8 }}>
                                O atendente pode atender em múltiplos locais de atendimento? Marque a opção acima para continuar.
                            </Typography>
                        )}

                        {availableProfiles.length === 0 && (
                            <Typography color="textSecondary">
                                Nenhum perfil disponível para conceder.
                            </Typography>
                        )}
                        {mutation.isError && !requiresMultipleOrgaosConfirmation && (
                            <Typography color="error" className={classes.textWarning}>
                                {(mutation.error as any)?.response?.data?.error || "Erro ao adicionar perfil."}
                            </Typography>
                        )}
                    </CardContent>
                    <CardActions className={classes.cardActions} style={{ flexShrink: 0 }}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={classes.duplicateButton}
                            disabled={
                                mutation.isLoading || 
                                !profile || 
                                (needsOrgao && !orgao) ||
                                (profile === 'Atendente' && userHasAtendenteProfile && requiresMultipleOrgaosConfirmation && !allowMultipleOrgaos)
                            }
                        >
                            {mutation.isLoading ? "Salvando" : "Salvar"}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
